/**
 * Registry des colonnes proposables sur /biens (liste + export PDF).
 *
 * Demande user 2026-05-07 : "lorsqu'on est connecté en CLEVEL, bouton qui
 * ouvre une popup permettant de choisir les colonnes (...) ce ne sont pas
 * tous les champs qu'on doit proposer mais une sélection".
 *
 * Source de vérité : ce fichier centralise (clé, label i18n, alignement,
 * visibilité par défaut, sortable). BiensListPage et BiensPrintPage iterent
 * sur la liste filtrée par les préférences utilisateur (localStorage).
 */

export type BiensColumnKey =
  | "ref"
  | "projet"
  | "adresse"
  | "codePostal"
  | "ville"
  | "region"
  | "collaborateur"
  | "disponibilite"
  | "dispoDepuis"
  | "seraDispoPour"
  | "publication"
  | "prix"
  | "charges"
  | "surface"
  | "nbChambres"
  | "nbSdb"
  | "etage"
  | "classePeb"
  | "pebFinLe"
  | "renonRecuLe"
  | "finDeContrat"
  | "meuble"
  | "companyContract"
  | "statutEls"
  | "mailingOk";

export interface BiensColumnDef {
  key: BiensColumnKey;
  /** i18n key pour l'entête */
  i18nLabel: string;
  align: "left" | "right";
  /** Visibilité par défaut (= les 10 colonnes historiques) */
  defaultVisible: boolean;
}

export const BIENS_COLUMNS: BiensColumnDef[] = [
  // ── 10 colonnes par défaut (= comportement historique) ──
  { key: "ref",            i18nLabel: "biens.reference",       align: "left",  defaultVisible: true },
  { key: "projet",         i18nLabel: "biens.projet",          align: "left",  defaultVisible: true },
  { key: "adresse",        i18nLabel: "common.address",        align: "left",  defaultVisible: true },
  { key: "region",         i18nLabel: "common.region",         align: "left",  defaultVisible: true },
  { key: "collaborateur",  i18nLabel: "common.collaborateur",  align: "left",  defaultVisible: true },
  { key: "disponibilite",  i18nLabel: "biens.availability",    align: "left",  defaultVisible: true },
  { key: "dispoDepuis",    i18nLabel: "biens.dispoDepuis",     align: "right", defaultVisible: true },
  { key: "publication",    i18nLabel: "biens.statutPublication", align: "left",  defaultVisible: true },
  { key: "prix",           i18nLabel: "biens.price",           align: "right", defaultVisible: true },
  { key: "surface",        i18nLabel: "biens.surface",         align: "right", defaultVisible: true },
  // ── Colonnes optionnelles (CLEVEL peut les activer via le picker) ──
  { key: "codePostal",     i18nLabel: "biens.columns.codePostal",     align: "left",  defaultVisible: false },
  { key: "ville",          i18nLabel: "biens.columns.ville",          align: "left",  defaultVisible: false },
  { key: "seraDispoPour",  i18nLabel: "biens.columns.seraDispoPour",  align: "left",  defaultVisible: false },
  { key: "charges",        i18nLabel: "biens.columns.charges",        align: "right", defaultVisible: false },
  { key: "nbChambres",     i18nLabel: "biens.columns.nbChambres",     align: "right", defaultVisible: false },
  { key: "nbSdb",          i18nLabel: "biens.columns.nbSdb",          align: "right", defaultVisible: false },
  { key: "etage",          i18nLabel: "biens.columns.etage",          align: "right", defaultVisible: false },
  { key: "classePeb",      i18nLabel: "biens.columns.classePeb",      align: "left",  defaultVisible: false },
  { key: "pebFinLe",       i18nLabel: "biens.columns.pebFinLe",       align: "left",  defaultVisible: false },
  { key: "renonRecuLe",    i18nLabel: "biens.columns.renonRecuLe",    align: "left",  defaultVisible: false },
  { key: "finDeContrat",   i18nLabel: "biens.columns.finDeContrat",   align: "left",  defaultVisible: false },
  { key: "meuble",         i18nLabel: "biens.columns.meuble",         align: "left",  defaultVisible: false },
  { key: "companyContract",i18nLabel: "biens.columns.companyContract",align: "left",  defaultVisible: false },
  { key: "statutEls",      i18nLabel: "biens.columns.statutEls",      align: "left",  defaultVisible: false },
  // Demande user 2026-05-08 : colonne "Mailing OK" pour spotter d'un coup
  // d'œil les biens à compléter avant diffusion. Badge OK / Manquant via
  // validateBienPrep. Optionnelle (l'admin l'active via le picker).
  { key: "mailingOk",      i18nLabel: "biens.columns.mailingOk",      align: "left",  defaultVisible: false },
];

export const DEFAULT_VISIBLE_COLUMNS: BiensColumnKey[] =
  BIENS_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);

const STORAGE_KEY = "skyline-biens-columns-v1";

/** Lit les préférences depuis localStorage. Fallback = colonnes par défaut. */
export function loadVisibleColumns(): BiensColumnKey[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE_COLUMNS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_VISIBLE_COLUMNS;
    // Filtre les clés inconnues (forward-compat si on retire une colonne)
    const known = new Set(BIENS_COLUMNS.map((c) => c.key));
    const valid = parsed.filter((k): k is BiensColumnKey => typeof k === "string" && known.has(k as BiensColumnKey));
    return valid.length > 0 ? valid : DEFAULT_VISIBLE_COLUMNS;
  } catch {
    return DEFAULT_VISIBLE_COLUMNS;
  }
}

export function saveVisibleColumns(keys: BiensColumnKey[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // localStorage indisponible (mode privé...) → on ignore, pas critique.
  }
}

/** Renvoie les défs ordonnées selon l'ordre choisi par l'utilisateur dans `visible`.
 *  Ignore les clés inconnues. L'ordre de la sélection prime sur celui du registry. */
export function getOrderedVisibleColumns(visible: BiensColumnKey[]): BiensColumnDef[] {
  const byKey = new Map(BIENS_COLUMNS.map((c) => [c.key, c]));
  const out: BiensColumnDef[] = [];
  for (const k of visible) {
    const def = byKey.get(k);
    if (def) out.push(def);
  }
  return out;
}

/** Liste les colonnes encore disponibles (= non sélectionnées) dans l'ordre du registry. */
export function getAvailableColumns(visible: BiensColumnKey[]): BiensColumnDef[] {
  const visibleSet = new Set(visible);
  return BIENS_COLUMNS.filter((c) => !visibleSet.has(c.key));
}
